import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Archive,
    Building2,
    Ship,
    Users,
    Droplet,
    Wrench,
    ArrowRight,
    MessageCircle,
    Cpu,
    ShieldCheck,
    Handshake,
    ClipboardList,
    HardHat,
    Truck,
    Warehouse,
    Package,
    Zap,
    Factory,
    ShoppingBag,
    Wind,
    CalendarDays,
    Star,
    ChevronDown,
} from "lucide-react";
import useSeo from "@/hooks/useSeo";
import SectionHeading from "@/components/common/SectionHeading";
import ProductCard from "@/components/products/ProductCard";
import BlogCard from "@/components/blogs/BlogCard";
import WhatsAppButton from "@/components/whatsapp/WhatsAppButton";
import StructuredData from "@/components/seo/StructuredData";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { getFeaturedProducts } from "@/services/products.service";
import { getBlogPosts } from "@/services/blogs.service";
import { getPageSeo } from "@/services/seo.service";
import {
    defaultEnquiryMessage,
    customEnquiryMessage,
} from "@/utilities/whatsapp";
import { faqSchema } from "@/utilities/seo";
import { trustHighlights } from "@/config/site.config";

const containerFinder = [
    {
        icon: Archive,
        title: "Storage Container",
        description:
            "Secure, weather-resistant storage for equipment, inventory, documents and site materials.",
        href: "/containers/storage-containers",
    },
    {
        icon: Building2,
        title: "Office Container",
        description:
            "Portable workspaces with doors, windows, lighting, electrical points and air-conditioning provision.",
        href: "/containers/office-containers",
    },
    {
        icon: Ship,
        title: "Cargo-Worthy Container",
        description: "Containers suitable for shipping, freight and cargo movement operations.",
        href: "/containers/cargo-worthy-containers",
    },
    {
        icon: Users,
        title: "Workers' Quarters",
        description: "Container accommodation units for site workers and project teams.",
        href: "/containers/workers-quarters",
    },
    {
        icon: Droplet,
        title: "Sanitary or Toilet Container",
        description: "Container-based toilet and sanitary facilities for sites and events.",
        href: "/containers/sanitary-containers",
    },
    {
        icon: Wrench,
        title: "Customised Container",
        description:
            "Purpose-built container solutions designed around your operational or commercial requirements.",
        href: "/containers/customised-containers",
    },
];

const customisationOptions = [
    "Personnel doors",
    "Sliding / roller-shutter doors",
    "Windows",
    "Internal partitions",
    "Insulation",
    "Wall & ceiling lining",
    "Electrical wiring",
    "Lighting",
    "Power outlets",
    "Air-conditioning provision",
    "Ventilation",
    "Plumbing",
    "Sanitary fixtures",
    "Flooring",
    "Shelving",
    "Security features",
    "External repainting",
    "Brand colours",
    "Signage",
    "Special openings",
];

const applications = [
    { icon: HardHat, label: "Construction Sites", text: "Secure on-site storage, offices and welfare units." },
    { icon: Factory, label: "Industrial Facilities", text: "Additional storage or facility footprint on demand." },
    { icon: Warehouse, label: "Warehousing & Storage", text: "Expand storage capacity with quick deployment." },
    { icon: Truck, label: "Logistics Operations", text: "Cargo-worthy units for freight and yard operations." },
    { icon: Building2, label: "Temporary Offices", text: "Portable offices with services and finishes." },
    { icon: Users, label: "Workers' Facilities", text: "Accommodation, meal and rest units." },
    { icon: ShieldCheck, label: "Security Posts", text: "Compact guard houses for site access control." },
    { icon: Droplet, label: "Sanitary Facilities", text: "Toilets, showers and hand-wash stations." },
    { icon: CalendarDays, label: "Events & Temporary Spaces", text: "Short-term deployments for events and pop-ups." },
    { icon: ShoppingBag, label: "Retail / Commercial Concepts", text: "Kiosks, brand activations and retail units." },
    { icon: Package, label: "Equipment Storage", text: "Weatherproof storage of tools and machinery." },
    { icon: Cpu, label: "Special Projects", text: "Purpose-built enclosures and equipment rooms." },
];

const whyChoose = [
    { icon: Star, title: "Experience Since 2010", text: "More than a decade supporting Singapore customers with practical container solutions." },
    { icon: Package, title: "New, Used & Customised Options", text: "Compare conditions, sizes and configurations to match your needs and budget." },
    { icon: Handshake, title: "Reasonable Pricing", text: "Suitable options and practical value without unnecessary complexity." },
    { icon: Wind, title: "Wide Range of Applications", text: "Storage, offices, cargo, workers' facilities, sanitary units, guard houses and custom." },
    { icon: ClipboardList, title: "Personalised Recommendations", text: "Speak directly with our team to plan the right container for your use." },
    { icon: MessageCircle, title: "Convenient WhatsApp Enquiries", text: "Share requirements, reference images and project details on WhatsApp." },
];

const howItWorks = [
    { step: "01", title: "Tell Us What You Need", text: "Share the intended use, preferred container size, location and required timeline." },
    { step: "02", title: "Review Suitable Options", text: "Our team will discuss available new, used or customised container options." },
    { step: "03", title: "Confirm Specifications", text: "Confirm the container condition, layout, modifications and other applicable requirements." },
    { step: "04", title: "Arrange the Next Steps", text: "Our team will advise on availability, pricing and applicable delivery or placement arrangements." },
];

const faqs = [
    { question: "Do you sell both new and used containers?", answer: "Yes. Container Specialist offers both new and used container options, subject to current availability." },
    { question: "What container sizes are available?", answer: "Common options include 20ft × 8ft, 40ft × 8ft, 20ft × 10ft and 40ft × 10ft. Contact the team to confirm current availability and suitable configurations." },
    { question: "Can a container be customised?", answer: "Yes. Containers may be modified for uses such as offices, guard houses, workers' facilities, sanitary units, storage areas and other project-specific applications." },
    { question: "How do I request a quotation?", answer: "Send your requirements through WhatsApp, including the intended use, preferred size, quantity, location and any customisation requirements." },
    { question: "Do you display prices on the website?", answer: "Prices may vary according to container size, condition, availability and modification requirements. Customers should contact Container Specialist for current pricing." },
    { question: "What should I include in my enquiry?", answer: "Include the container size, quantity, intended use, preferred condition, delivery location, expected timeline and any required modifications." },
    { question: "Are all containers immediately available?", answer: "Availability changes over time. Please contact the team to confirm the latest available listings." },
    { question: "Can I arrange to discuss a custom project?", answer: "Yes. Send your project details, sketches or reference images through WhatsApp for an initial discussion." },
];

export default function HomePage() {
    const [products, setProducts] = useState([]);
    const [blogs, setBlogs] = useState([]);
    const [seo, setSeo] = useState(null);

    useEffect(() => {
        Promise.all([getFeaturedProducts(6), getBlogPosts({ pageSize: 3 }), getPageSeo("home")]).then(
            ([p, b, s]) => {
                setProducts(p);
                setBlogs(b.items);
                setSeo(s);
            },
        );
    }, []);

    useSeo(seo || undefined, "/");

    return (
        <>
            {/* ============================================================= HERO */}
            <section className="relative bg-cs-charcoal text-white overflow-hidden" data-testid="home-hero">
                <div className="absolute inset-0">
                    {/* Hosted in the project's own Storage bucket rather than
                        hot-linked from a stock CDN, so the site's most prominent
                        image cannot break outside our control. */}
                    <img
                        src="https://firebasestorage.googleapis.com/v0/b/container-specialist.firebasestorage.app/o/site%2F1784546212555-hero.jpeg?alt=media&token=b36eba34-a4d8-41c4-b3af-d6b0432d6224"
                        alt="Container terminal in Singapore with stacked shipping containers and the city skyline beyond"
                        className="w-full h-full object-cover cs-kenburns"
                        fetchPriority="high"
                    />
                    <div className="absolute inset-0 bg-cs-navy/75" aria-hidden="true" />
                    <div className="absolute inset-0 cs-corrugated" aria-hidden="true" />
                </div>
                <div className="cs-container relative py-24 md:py-36 min-h-[80vh] flex items-center">
                    <div className="max-w-3xl">
                        <div className="cs-fade-up inline-flex items-center gap-3 border border-white/25 bg-white/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.28em] text-white/90">
                            <span className="w-1.5 h-1.5 bg-cs-orange cs-pulse-dot" />
                            Container Specialist · Singapore
                        </div>
                        <h1 className="cs-fade-up mt-6 font-heading uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight">
                            New, Used <span className="text-cs-orange">&</span> Customised
                            <br />
                            Containers in Singapore
                        </h1>
                        <p className="cs-fade-up mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
                            From secure storage and cargo-worthy units to offices, workers&apos; quarters,
                            sanitary facilities and purpose-built container conversions, we help
                            businesses find practical container solutions at reasonable prices.
                        </p>
                        <div className="cs-fade-up mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/containers"
                                className="cs-btn-primary"
                                data-testid="hero-browse-btn"
                            >
                                Browse Our Containers
                                <ArrowRight size={16} strokeWidth={2} />
                            </Link>
                            <WhatsAppButton
                                message={defaultEnquiryMessage}
                                label="WhatsApp for a Quote"
                                variant="ghost"
                                source="hero"
                                testId="hero-whatsapp-btn"
                            />
                        </div>
                        <ul className="cs-fade-up mt-10 flex flex-wrap gap-x-6 gap-y-3 font-mono text-[11px] uppercase tracking-[0.22em] text-white/75">
                            {trustHighlights.map((t) => (
                                <li key={t} className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-cs-orange" aria-hidden="true" />
                                    {t}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <a
                    href="#container-finder"
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:inline-flex items-center gap-2 text-white/70 hover:text-white font-mono text-[10px] uppercase tracking-[0.28em]"
                    aria-label="Scroll to container finder"
                >
                    Explore
                    <ChevronDown size={14} strokeWidth={2} className="animate-bounce" />
                </a>
            </section>

            {/* ================================================ CONTAINER FINDER */}
            <section id="container-finder" className="cs-container py-20 md:py-24" data-testid="home-container-finder">
                <SectionHeading
                    eyebrow="Container Finder"
                    title="What Type of Container Do You Need?"
                    description="Start with your intended use — from site storage to workers' quarters and custom conversions."
                />
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {containerFinder.map(({ icon: Icon, title, description, href }) => (
                        <Link
                            key={title}
                            to={href}
                            className="group cs-card cs-card-hover p-6 flex flex-col gap-4"
                            data-testid={`container-finder-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="w-12 h-12 inline-flex items-center justify-center border border-cs-steel/40 group-hover:border-cs-orange group-hover:text-cs-orange transition-colors text-cs-navy">
                                    <Icon size={22} strokeWidth={1.75} />
                                </span>
                                <span className="cs-overline-muted group-hover:text-cs-orange transition-colors">
                                    View Options →
                                </span>
                            </div>
                            <h3 className="font-heading uppercase text-2xl text-cs-navy group-hover:text-cs-orange transition-colors">
                                {title}
                            </h3>
                            <p className="text-sm text-cs-steel leading-relaxed">{description}</p>
                        </Link>
                    ))}
                </div>
            </section>

            {/* ================================================ 3 MAIN CATEGORIES */}
            <section className="bg-white border-y border-cs-steel/15 py-20 md:py-24" data-testid="home-main-categories">
                <div className="cs-container">
                    <SectionHeading
                        eyebrow="Explore Our Container Range"
                        title="New, Used & Customised — Chosen to Suit"
                        description="Compare our three primary categories and go deeper on the ones relevant to your project."
                    />
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                slug: "new-containers",
                                title: "New Containers",
                                text: "Clean, durable container units suited for customers seeking long-term reliability, a professional appearance and a strong foundation for customisation.",
                                cta: "View New Containers",
                                image:
                                    "https://images.unsplash.com/photo-1706499856012-14f062c72b49?auto=format&fit=crop&w=1200&q=80",
                            },
                            {
                                slug: "used-containers",
                                title: "Used Containers",
                                text: "Cost-effective and practical container solutions for storage, site operations, cargo use and modification projects.",
                                cta: "View Used Containers",
                                image:
                                    "https://images.unsplash.com/photo-1543709323-1f653b58d995?auto=format&fit=crop&w=1200&q=80",
                            },
                            {
                                slug: "customised-containers",
                                title: "Customised Containers",
                                text: "Containers transformed into functional spaces such as offices, guard houses, workers' facilities, sanitary units and other purpose-built solutions.",
                                cta: "Explore Customisation",
                                image:
                                    "https://images.pexels.com/photos/21533662/pexels-photo-21533662.jpeg?auto=compress&cs=tinysrgb&w=1200",
                            },
                        ].map((c) => (
                            <Link
                                key={c.slug}
                                to={`/containers/${c.slug}`}
                                className="group relative overflow-hidden border border-cs-steel/25 bg-cs-charcoal text-white flex flex-col"
                                data-testid={`home-category-card-${c.slug}`}
                            >
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img
                                        src={c.image}
                                        alt={c.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.06]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-cs-charcoal/85" aria-hidden="true" />
                                </div>
                                <div className="p-6 relative -mt-24 flex flex-col gap-3">
                                    <p className="cs-overline">Range</p>
                                    <h3 className="font-heading uppercase text-3xl">{c.title}</h3>
                                    <p className="text-sm text-white/80 leading-relaxed">{c.text}</p>
                                    <div className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-cs-orange group-hover:text-white transition-colors">
                                        {c.cta}
                                        <ArrowRight size={12} strokeWidth={2} />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================================================= FEATURED LISTINGS */}
            <section className="cs-container py-20 md:py-24" data-testid="home-featured-listings">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <SectionHeading
                        eyebrow="Featured Container Listings"
                        title="A Snapshot of Current Availability"
                        description="Explore selected containers our team is featuring this month. Full catalogue and filtering available on the Our Containers page."
                    />
                    <Link to="/containers" className="cs-btn-outline-dark" data-testid="home-view-all-containers-btn">
                        View All Containers
                        <ArrowRight size={14} strokeWidth={2} />
                    </Link>
                </div>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>
            </section>

            {/* ================================================= CUSTOMISATION */}
            <section className="cs-container py-20 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10" data-testid="home-customisation">
                <div className="relative overflow-hidden bg-cs-charcoal aspect-[4/3] lg:aspect-auto">
                    <img
                        src="https://images.pexels.com/photos/21533662/pexels-photo-21533662.jpeg?auto=compress&cs=tinysrgb&w=1400"
                        alt="Modified shipping container being customised"
                        loading="lazy"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-4 left-4 right-4 flex justify-between font-mono text-[10px] uppercase tracking-widest text-white/85">
                        <span>Customised · Site Office</span>
                        <span>Made-to-Order</span>
                    </div>
                </div>
                <div className="flex flex-col justify-center">
                    <p className="cs-overline">Built Around Your Requirements</p>
                    <h2 className="mt-2 font-heading uppercase text-4xl md:text-5xl text-cs-navy leading-[1.02]">
                        Turn a Container Into a Space That Works for You
                    </h2>
                    <p className="mt-4 text-cs-steel leading-relaxed">
                        A shipping container can be much more than a storage unit. Our team can help
                        you plan customised layouts and modifications based on the intended use,
                        available space and operational requirements.
                    </p>
                    <ul className="mt-6 grid grid-cols-2 gap-1.5">
                        {customisationOptions.map((o) => (
                            <li
                                key={o}
                                className="font-mono text-[11px] uppercase tracking-[0.16em] text-cs-charcoal border-l-2 border-cs-orange pl-2 py-1"
                            >
                                {o}
                            </li>
                        ))}
                    </ul>
                    <p className="mt-4 text-xs text-cs-steel italic">
                        Customisation options are subject to technical feasibility, intended
                        application and project requirements.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <WhatsAppButton
                            message={customEnquiryMessage}
                            label="Discuss Your Custom Container"
                            variant="primary"
                            source="home-customisation"
                            testId="home-custom-whatsapp-btn"
                        />
                        <Link to="/containers/customised-containers" className="cs-btn-outline-dark" data-testid="home-custom-explore-btn">
                            Explore Customisation
                            <ArrowRight size={14} strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ================================================= APPLICATIONS */}
            <section className="bg-white border-y border-cs-steel/15 py-20 md:py-24" data-testid="home-applications">
                <div className="cs-container">
                    <SectionHeading
                        eyebrow="Practical Solutions for Different Applications"
                        title="Where Our Containers Get Used"
                    />
                    <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {applications.map(({ icon: Icon, label, text }) => (
                            <div
                                key={label}
                                className="cs-card p-5 hover:border-cs-orange transition-colors group"
                            >
                                <Icon
                                    className="text-cs-navy group-hover:text-cs-orange transition-colors"
                                    size={22}
                                    strokeWidth={1.75}
                                />
                                <p className="mt-3 font-heading uppercase text-lg text-cs-navy">
                                    {label}
                                </p>
                                <p className="mt-1 text-xs text-cs-steel leading-relaxed">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================================================= WHY CHOOSE */}
            <section className="cs-container py-20 md:py-24" data-testid="home-why-choose">
                <SectionHeading
                    eyebrow="Why Businesses Choose Container Specialist"
                    title="Reliable, Flexible, Reasonably Priced"
                />
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {whyChoose.map(({ icon: Icon, title, text }) => (
                        <div key={title} className="cs-card p-6">
                            <div className="flex items-center gap-3">
                                <span className="w-10 h-10 inline-flex items-center justify-center border border-cs-orange text-cs-orange">
                                    <Icon size={18} strokeWidth={1.75} />
                                </span>
                                <p className="cs-overline-muted">/{title.split(" ")[0].toLowerCase()}</p>
                            </div>
                            <h3 className="mt-4 font-heading uppercase text-xl text-cs-navy">{title}</h3>
                            <p className="mt-2 text-sm text-cs-steel leading-relaxed">{text}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ================================================= HOW IT WORKS */}
            <section className="bg-cs-navy text-white cs-corrugated py-20 md:py-24" data-testid="home-how-it-works">
                <div className="cs-container">
                    <SectionHeading
                        eyebrow="How It Works"
                        title="From Enquiry to Placement"
                        className="[&_h2]:text-white"
                    />
                    <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {howItWorks.map((s, i) => (
                            <li
                                key={s.step}
                                className="relative border-t-2 border-cs-orange bg-white/5 backdrop-blur-sm p-6"
                            >
                                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-cs-orange">
                                    Step {s.step}
                                </p>
                                <h3 className="mt-2 font-heading uppercase text-2xl text-white">
                                    {s.title}
                                </h3>
                                <p className="mt-2 text-sm text-white/75 leading-relaxed">{s.text}</p>
                                {i < howItWorks.length - 1 && (
                                    <ArrowRight
                                        size={18}
                                        strokeWidth={2}
                                        className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 text-cs-orange"
                                    />
                                )}
                            </li>
                        ))}
                    </ol>
                    <p className="mt-10 text-xs text-white/60 max-w-2xl font-mono uppercase tracking-widest">
                        Final availability, pricing, delivery, placement and modification arrangements are subject to confirmation by Container Specialist.
                    </p>
                </div>
            </section>

            {/* ================================================= LATEST BLOGS */}
            <section className="cs-container py-20 md:py-24" data-testid="home-latest-blogs">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <SectionHeading eyebrow="Latest Insights" title="Container Guides & Ideas" />
                    <Link to="/blogs" className="cs-btn-outline-dark" data-testid="home-view-all-blogs-btn">
                        View All Articles
                        <ArrowRight size={14} strokeWidth={2} />
                    </Link>
                </div>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {blogs.map((b) => (
                        <BlogCard key={b.id} post={b} />
                    ))}
                </div>
            </section>

            {/* ================================================= FAQ */}
            <section className="bg-white border-y border-cs-steel/15 py-20 md:py-24" data-testid="home-faq">
                <div className="cs-container grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10">
                    <div>
                        <SectionHeading
                            eyebrow="Frequently Asked"
                            title="Answers to Common Container Questions"
                            description="Can&apos;t find what you need? Send us your specific query on WhatsApp and our team will respond."
                        />
                        <div className="mt-6">
                            <WhatsAppButton
                                message={defaultEnquiryMessage}
                                label="Ask on WhatsApp"
                                variant="whatsapp"
                                source="home-faq"
                                testId="home-faq-whatsapp-btn"
                            />
                        </div>
                    </div>
                    <Accordion type="single" collapsible className="w-full" data-testid="home-faq-accordion">
                        {faqs.map((f, i) => (
                            <AccordionItem
                                key={f.question}
                                value={`item-${i}`}
                                className="border-b border-cs-steel/25"
                            >
                                <AccordionTrigger
                                    className="font-heading text-lg uppercase text-cs-navy hover:text-cs-orange hover:no-underline data-[state=open]:text-cs-orange"
                                    data-testid={`home-faq-trigger-${i}`}
                                >
                                    {f.question}
                                </AccordionTrigger>
                                <AccordionContent className="text-sm text-cs-steel leading-relaxed">
                                    {f.answer}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
                <StructuredData data={faqSchema(faqs)} />
            </section>

            {/* ================================================= FINAL CTA */}
            <section className="relative overflow-hidden bg-cs-charcoal text-white" data-testid="home-final-cta">
                <div className="absolute inset-0">
                    <img
                        src="https://images.pexels.com/photos/32865462/pexels-photo-32865462.jpeg?auto=compress&cs=tinysrgb&w=1800"
                        alt="Container yard"
                        loading="lazy"
                        className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-cs-charcoal/85" aria-hidden="true" />
                    <div className="absolute inset-0 cs-corrugated" aria-hidden="true" />
                </div>
                <div className="cs-container relative py-24 md:py-28 text-center max-w-3xl mx-auto">
                    <p className="cs-overline justify-center inline-flex">Ready to Enquire?</p>
                    <h2 className="mt-3 font-heading uppercase text-4xl md:text-5xl lg:text-6xl">
                        Need Help Choosing the Right Container?
                    </h2>
                    <p className="mt-4 text-white/80 leading-relaxed">
                        Tell us what you need, where it will be used and whether you prefer a new,
                        used or customised unit. Our team will help you explore the available options.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <WhatsAppButton
                            message={defaultEnquiryMessage}
                            label="WhatsApp Container Specialist"
                            variant="whatsapp"
                            source="home-final-cta"
                            testId="home-final-cta-whatsapp-btn"
                        />
                        <Link to="/containers" className="cs-btn-ghost" data-testid="home-final-cta-browse-btn">
                            Browse Available Containers
                            <ArrowRight size={14} strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
